import { Logo } from "@/app/Logo";
import { brand } from "@/brand";

import { IA_TOP_CITIES, INDEX_NAMES, INDEX_SLUGS, TOP_CITIES, TOP_STOCKS, stockHref } from "./links";
import { SiteLink } from "./SiteLink";
import { useMemberTrial } from "./trials";
import { WhatsAppChat } from "./WhatsAppChat";

/** The GSTIN / grievance officer / phone / email line in the footer (hidden for now). */
const SHOW_COMPANY_DETAILS = false;

const YEAR = new Date().getFullYear();

/** Company and policy pages (src/pages/legal); payment partners look for these. */
const COMPANY: [string, string][] = [
  ["/about", "About us"],
  ["/contact", "Contact us"],
  ["/pricing", "Pricing"],
  ["/services", "Services"],
  ["/how-it-works", "How it works (videos)"],
  ["/free-trial", "7-day free trial"],
  ["/faq", "FAQ"],
  ["/terms", "Terms and conditions"],
  ["/privacy", "Privacy policy"],
  ["/refund-policy", "Cancellation and refunds"],
  ["/delivery-policy", "Service delivery"],
  ["/disclaimer", "Disclaimer"],
];

const MARKETS: [string, string][] = [
  ["/share-market-today", "Share market today"],
  ["/top-gainers", "Top gainers"],
  ["/top-losers", "Top losers"],
  ["/most-active", "Most active"],
  ["/52-week-high", "52-week high"],
  ["/52-week-low", "52-week low"],
  ["/stocks", "NIFTY 500 stocks"],
  ["/sectors", "Sectors"],
];

const INDICES: [string, string][] = Object.entries(INDEX_SLUGS).map(([sym, slug]) => [
  `/indices/${slug}`,
  INDEX_NAMES[sym] ?? sym,
]);

const COLUMNS: { title: string; links: [string, string][] }[] = [
  { title: "Company", links: COMPANY },
  { title: "Markets", links: MARKETS },
  { title: "Indices", links: [...INDICES, ["/indices", "All indices"]] },
  { title: "Top stocks", links: TOP_STOCKS.slice(0, 8).map(([sym, name]) => [stockHref(sym), name]) },
  {
    title: "Research Analysts",
    links: [
      ["/find-research-analyst", "Find a Research Analyst"],
      ...TOP_CITIES.slice(0, 6).map(([c, rel]): [string, string] => [`/research-analysts/${rel}`, `In ${c}`]),
      ["/research-analysts", "All states"],
      ["/for-research-analysts", "Join as a Research Analyst"],
    ],
  },
  {
    title: "Investment Advisers",
    links: [
      ["/find-investment-adviser", "Find an Investment Adviser"],
      ...IA_TOP_CITIES.map(([c, rel]): [string, string] => [`/investment-advisers/${rel}`, `In ${c}`]),
      ["/investment-advisers", "All states"],
    ],
  },
];

/** The public site's footer: link columns, then the operator's details and the regulatory text. */
export function SiteFooter() {
  const owner = brand.legalName || brand.name;
  // The free-trial link follows the member switch (Admin → Plans & trials → Sign-up & trial).
  const trial = useMemberTrial();
  const columns = COLUMNS.map((col) =>
    col.links === COMPANY
      ? {
          ...col,
          links: COMPANY.filter(([to]) => to !== "/free-trial" || trial).map(([to, label]): [string, string] =>
            to === "/free-trial" ? [to, `${trial}-day free trial`] : [to, label],
          ),
        }
      : col,
  );
  return (
    <footer className="bg-ink text-ink-muted">
      {/* every public page has this footer: the floating "Chat on WhatsApp" button rides with it */}
      <WhatsAppChat />
      <div className="site-wrap pt-12 pb-8">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-ink-border pb-8">
          <span className="inline-flex items-center gap-2">
            <Logo withName={false} />
            <span className="font-display text-xl font-semibold text-ink-fg">{brand.name}</span>
          </span>
          {brand.supportEmail ? (
            <a className="text-sm text-ink-fg underline-offset-4 hover:underline" href={`mailto:${brand.supportEmail}`}>
              {brand.supportEmail}
            </a>
          ) : null}
        </div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 py-10 sm:grid-cols-3 lg:grid-cols-6">
          {columns.map((col) => (
            <nav key={col.title} aria-label={col.title} className="min-w-0 text-sm">
              <h2 className="font-display text-base font-semibold tracking-wide text-ink-fg">{col.title}</h2>
              <ul className="mt-4 space-y-2.5">
                {col.links.map(([to, label]) => (
                  <li key={to + label} className="min-w-0">
                    <SiteLink to={to} className="block truncate text-ink-muted transition-colors hover:text-ink-fg">
                      {label}
                    </SiteLink>
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>
        <div className="space-y-3 border-t border-ink-border pt-8 text-xs leading-relaxed">
          <p className="text-ink-fg">
            <strong className="font-semibold">{owner}</strong>
            {brand.location ? <> · {brand.location}</> : null}
          </p>
          {/* GSTIN, grievance officer, phone and email: hidden for now (owner, 2026-10-25) */}
          {SHOW_COMPANY_DETAILS ? (
            <p>
              {brand.gstin ? <>GSTIN {brand.gstin}</> : null}
              {brand.grievanceOfficer ? <> · Grievance officer: {brand.grievanceOfficer}</> : null}
              {brand.phone ? (
                <>
                  {" "}·{" "}
                  <a className="text-ink-fg hover:underline" href={`tel:${brand.phone.replace(/\s+/g, "")}`}>
                    {brand.phone}
                  </a>
                </>
              ) : null}
              {brand.supportEmail ? (
                <>
                  {" "}·{" "}
                  <a className="text-ink-fg [overflow-wrap:anywhere] hover:underline" href={`mailto:${brand.supportEmail}`}>
                    {brand.supportEmail}
                  </a>
                </>
              ) : null}
            </p>
          ) : null}
          <p>
            {brand.name} is a market research tools platform operated by {owner}. It provides market data,
            analytics, backtests and education. It does not give investment advice or recommendations, does not
            handle client funds or securities and does not place orders. The Research Analyst and Investment
            Adviser directories come from SEBI&apos;s public registers; a listing is not an endorsement.
          </p>
          <p>
            Investments in securities market are subject to market risks. Read all the related documents
            carefully before investing. Registration granted by SEBI and certification from NISM in no way
            guarantee the performance of the intermediary or provide any assurance of returns to investors.
            Past performance, backtests and simulations do not indicate future returns.
          </p>
          <p>Market data is from NSE and BSE public files and broker feeds, may be delayed, and is for information only.</p>
          <p className="pt-2 text-ink-fg">
            © {YEAR} {owner}. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}
